import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import { DesignSystemRecipe } from 'designSystem/types';
import { groupTokensByTheme } from 'designSystem/content/util';
import TokenGrid from '../tokens/TokenGrid';
import * as Base from '../styles';
import * as S from './styles';

const StylesView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, selectedDsPicks, activeTab } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			selectedDsPicks: r.mode.selectedDsPicks,
			activeTab: selectActiveDsTab(r.mode),
		}),
		shallowEqual
	);
	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	const tokensToDisplay = useMemo(() => {
		if (!designSystemContent?.styles?.tokens) return [];
		return designSystemContent.styles.tokens.all || [];
	}, [designSystemContent?.styles?.tokens]);

	const modes = designSystemContent?.config?.display?.modes ?? [];
	const modeIds = modes.map((m) => m.id);
	const defaultModeId = modes.find((m) => m.default)?.id ?? modes[0]?.id;

	const groupedByTheme = useMemo(() => {
		return groupTokensByTheme(tokensToDisplay, modeIds);
	}, [tokensToDisplay, modeIds]);

	const nonDefaultModeIds = modeIds.filter((id) => id !== defaultModeId);

	const recipesByDomain = useMemo(() => {
		if (!designSystemContent?.styles?.recipes) return {};
		return designSystemContent.styles.recipes.byDomain;
	}, [designSystemContent?.styles?.recipes]);

	const recipeDomains = useMemo(() => {
		return Object.keys(recipesByDomain).sort();
	}, [recipesByDomain]);

	const isRecipeSelected = (className: string) =>
		selectedDsPicks.some((p) => p.kind === 'recipe' && p.className === className);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderRecipeCard = (recipe: DesignSystemRecipe) => (
		<S.RecipeCard
			key={recipe.id}
			$selected={isRecipeSelected(recipe.className)}
			onClick={() => dispatch(toggleDsPick({ kind: 'recipe', className: recipe.className }))}
		>
			<S.RecipeHeader>
				<S.RecipeName>{recipe.name}</S.RecipeName>
			</S.RecipeHeader>
			<S.RecipeClassName>{recipe.className}</S.RecipeClassName>
			{recipe.description && <S.RecipeDescription>{recipe.description}</S.RecipeDescription>}
			<S.RecipeProperties>
				{recipe.properties.map((prop) => (
					<S.RecipeProperty key={prop.property}>
						<S.PropertyName>{prop.property}</S.PropertyName>:{' '}
						<S.PropertyValue>{prop.value}</S.PropertyValue>
					</S.RecipeProperty>
				))}
			</S.RecipeProperties>
		</S.RecipeCard>
	);

	const hasTokens = tokensToDisplay.length > 0;
	const hasRecipes = recipeDomains.length > 0;
	const isEmpty = !hasTokens && !hasRecipes;

	return (
		<Base.SectionBase ref={scrollRef}>
			<Base.SectionHeader>
				<Base.SectionTitle>Styles</Base.SectionTitle>
			</Base.SectionHeader>
			<Base.SectionContent>
				{isEmpty && <p>No style tokens or recipes found.</p>}

				{hasTokens && (
					<>
						{hasRecipes && <S.SubSectionTitle>Tokens</S.SubSectionTitle>}
						{groupedByTheme.base.length > 0 && (
							<TokenGrid tokens={groupedByTheme.base} />
						)}
						{nonDefaultModeIds.map((modeId) => {
							const tokens = groupedByTheme[modeId] ?? [];
							if (tokens.length === 0) return null;
							const modeLabel = modes.find((m) => m.id === modeId)?.label ?? modeId;
							return (
								<div key={modeId}>
									<Base.SubSectionTitle>{modeLabel} Overrides</Base.SubSectionTitle>
									<TokenGrid tokens={tokens} />
								</div>
							);
						})}
					</>
				)}

				{hasRecipes && (
					<>
						{hasTokens && <S.SubSectionTitle>Recipes</S.SubSectionTitle>}
						{recipeDomains.map((domain) => (
							<div key={domain}>
								<S.DomainHeader>{domain}</S.DomainHeader>
								<S.RecipeGrid>{recipesByDomain[domain].map(renderRecipeCard)}</S.RecipeGrid>
							</div>
						))}
					</>
				)}
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default StylesView;
